import { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { faCheckCircle, faClock } from '@fortawesome/free-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { LayoutContext } from '@/contexts/LayoutContext';
import { useEnv } from '@/env/client';
import { useFormApi } from '@lamesarv-sdk/hooks';
import { navigate } from '@lamesarv-sdk/tools';
import { useDynamicsForms } from '@/hooks/dynamicsForms';
import { useDevice } from '@/hooks/useDevice';
import { parsePhone } from '@/tools/dynamics';
import { withSuspense } from '@/tools/withSuspense';

import { RichText } from '@lamesarv-sdk/components';
import { CommonForm } from '@/components/CommonForm';
import { CommonHidden } from '@/components/CommonForm/CommonHidden';
import { CommonInput } from '@/components/CommonForm/CommonInput';
import { CommonLocationSelect } from '@/components/CommonForm/CommonLocationSelect';
import { CommonMaskInput } from '@/components/CommonForm/CommonMaskInput';
import { CommonTextarea } from '@/components/CommonForm/CommonTextarea';

import { BaseOptInCheckbox, BaseSubmit } from '../base';
import { IFormSchema, validationSchema } from './schema';

import {
  DynamicsLeadSource,
  DynamicsLeadType,
  DynamicsServiceCenterType,
  IDynamicsFormMapping,
} from '@/types/dynamicsForms';
import { IFormProps, IFormResetterProps } from '../types';

import * as FormClasses from '../classes';

const FormResetter = (props: IFormResetterProps) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!props.wasSuccessful) return;

    reset();
    reset({ phone: '' });

    if (props.callback) {
      props.callback();
    }

    if (props.redirect) {
      navigate(props.redirect);
    }
  }, [props.wasSuccessful]);

  return null;
};

export const FormParts = withSuspense((props: IFormProps) => {
  const { context: layoutContext } = useContext(LayoutContext);
  const { fetchData: getDevice } = useDevice({ metadata: true, skipFetch: true });
  const { env } = useEnv();

  const [loadingLegacy, submittedLegacy, errorLegacy, sendFormLegacy] = useFormApi();
  const { loading, submitted, error, sendForm } = useDynamicsForms();

  const onSubmit = async (data: IFormSchema, metadata?: Record<string, unknown>) => {
    const formInput = { ...data };
    const formMappings: IDynamicsFormMapping[] = [
      {
        DataverseFieldName: 'lmrv_leadtype',
        DataverseFieldValue: String(DynamicsLeadType.parts),
      },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue: String(DynamicsLeadSource.website),
      },
      {
        FormFieldName: 'store',
        DataverseFieldName: 'lmrv_service_center',
        DataverseFieldValue: [
          { FormValue: 'Phoenix (Servicing Mesa), AZ', DataverseValue: DynamicsServiceCenterType.phoenix },
          { FormValue: 'Tucson, AZ', DataverseValue: DynamicsServiceCenterType.tucson },
          { FormValue: 'El Cajon (San Diego), CA', DataverseValue: DynamicsServiceCenterType.elCajon },
          { FormValue: 'Davis (Servicing West Sacramento), CA', DataverseValue: DynamicsServiceCenterType.davis },
          { FormValue: 'Orlando / Sanford, FL', DataverseValue: DynamicsServiceCenterType.orlando },
          { FormValue: 'Port St. Lucie (Servicing Davie), FL', DataverseValue: DynamicsServiceCenterType.portStLucie },
          { FormValue: 'Fort Myers, FL', DataverseValue: DynamicsServiceCenterType.ftMyers },
          { FormValue: 'Albuquerque, NM', DataverseValue: DynamicsServiceCenterType.albuquerque },
        ],
      },
      { FormFieldName: 'firstName', DataverseFieldName: 'firstname' },
      { FormFieldName: 'lastName', DataverseFieldName: 'lastname' },
      { DataverseFieldValue: parsePhone(formInput.phone), DataverseFieldName: 'mobilephone' },
      { FormFieldName: 'email', DataverseFieldName: 'emailaddress1' },
      { FormFieldName: 'year', DataverseFieldName: 'lmrv_vehicleyear' },
      { FormFieldName: 'make', DataverseFieldName: 'lmrv_make' },
      { FormFieldName: 'model', DataverseFieldName: 'lmrv_model' },
      { FormFieldName: 'vin', DataverseFieldName: 'lmrv_vin' },
      { FormFieldName: 'comments', DataverseFieldName: 'lmrv_comments' },
      {
        DataverseFieldName: 'lmrv_url',
        DataverseFieldValue: typeof location !== 'undefined' ? location?.href : '',
      },
      {
        DataverseFieldName: 'crbdb_segment',
        DataverseFieldValue: props.dynamics_segment,
      },
    ];

    if (env.ENABLE_FORMS_CAPTURE_IP) {
      const device = await getDevice();
      formInput.ip = device.ip;
      formInput.user_agent = device.userAgent;
      formInput.platform = device.platform;
      formMappings.push(
        { DataverseFieldName: 'lmrv_ip', DataverseFieldValue: device.ip },
        { DataverseFieldName: 'lmrv_useragent', DataverseFieldValue: device.userAgent },
        { DataverseFieldName: 'lmrv_platform', DataverseFieldValue: device.platform },
      );
    }

    if (env.ENABLE_FORMS_CAPTURE_GEO && !!layoutContext.geoLocation) {
      formInput.geo_coordinates = layoutContext.geoLocation.join(',');
      formMappings.push({
        DataverseFieldName: 'lmrv_geocoordinates',
        DataverseFieldValue: layoutContext.geoLocation.join(','),
      });
    }

    if (env.ENABLE_FORMS_CAPTURE_CAPTCHA_SCORE && env.ENABLE_FORMS_CAPTCHA) {
      formInput.captcha_score = metadata?.captchaScore ? Number(metadata?.captchaScore) : undefined;
      formInput.captcha_threshold = metadata?.captchaThreshold ? Number(metadata?.captchaThreshold) : undefined;
      formMappings.push(
        {
          DataverseFieldName: 'lmrv_captchascore',
          DataverseFieldValue: formInput.captcha_score,
        },
        {
          DataverseFieldName: 'lmrv_captchathreshold',
          DataverseFieldValue: formInput.captcha_threshold,
        },
      );
    }

    if (env.ENABLE_FORMS_LEGACY) {
      sendFormLegacy(formInput);
    }

    if (env.ENABLE_FORMS_DYNAMICS) {
      await sendForm({
        formId: `#${props.custom_id || 'formParts'}`,
        mappings: formMappings,
        dynamicsFormId: env.DYNAMICS_FORM_ID_PARTS,
      });
    }
  };

  const _loading = loading || loadingLegacy || false;
  const _error = error || errorLegacy || false;
  const _submitted = submitted || submittedLegacy || false;

  const wasSuccessful = !_loading && !_error && _submitted;

  // -- Prevent rendering if neither form system is enabled
  if (!env.ENABLE_FORMS_DYNAMICS && !env.ENABLE_FORMS_LEGACY) {
    return null;
  }

  return (
    <CommonForm
      id={props.custom_id || 'formParts'}
      schema={validationSchema}
      onSubmit={onSubmit}
      className={twMerge('font-helvetica relative', props.classes)}
      isDisabled={_loading || wasSuccessful}
      tagGroup={props.data_tag_group || 'form'}
      tagItem={props.data_tag ? `${props.data_tag}--form-parts` : 'form-parts'}
    >
      <CommonHidden name="formId" value="45" />
      <CommonHidden name="formType" value="parts" />
      <CommonHidden name="leadType" value="parts" />
      <CommonHidden name="leadSource" value="lamesarv.com" />
      <FormResetter wasSuccessful={wasSuccessful} callback={props.successCallback} redirect={props.redirect_url} />
      {props.extract_header && (
        <div
          className={twMerge('w-full md:w-[720px] xl:w-[865px] mx-auto flex flex-col gap-4 p-1', props.classes_header)}
        >
          {props.title && <div className={twMerge('heading-h4 leading-none', props.classes_title)}>{props.title}</div>}
          {props.description && (
            <div className={twMerge('text-md-regular', props.classes_description)}>
              <RichText content={props.description} />
            </div>
          )}
        </div>
      )}
      <div className={twMerge('w-full mx-auto flex flex-col gap-4 p-1 bg-white', props.classes_container)}>
        {!props.extract_header && (
          <div className={twMerge('flex flex-col justify-center items-center gap-4', props.classes_header)}>
            {props.title && (
              <div className={twMerge('heading-h4 leading-none', props.classes_title)}>{props.title}</div>
            )}
            {props.description && (
              <div className={twMerge('text-md-regular', props.classes_description)}>
                <RichText content={props.description} />
              </div>
            )}
          </div>
        )}
        {_loading && (
          <div
            className={
              'flex items-center w-full bg-gray-100 border border-gray-500 text-gray-700 py-2 px-3 mb-4 mx-2 shadow-md'
            }
            role="alert"
          >
            <FontAwesomeIcon icon={faClock} className="w-8 mr-2" />
            <span>Sending...</span>
          </div>
        )}
        {_submitted && !_error && (
          <div
            className={
              'flex items-center w-full bg-green-100 border border-green-500 text-green-700 py-2 px-3 mb-4 mx-2 shadow-md'
            }
            role="alert"
          >
            <FontAwesomeIcon icon={faCheckCircle} className="w-8 mr-2" />
            <span
              data-tag-group="form-success-label"
              data-tag={props.data_tag ? `${props.data_tag}--form-parts-success-label` : 'form-parts-success-label'}
            >
              Thank you for requesting more information.
            </span>
          </div>
        )}
        {_submitted && _error && (
          <div
            className={
              'flex items-center w-full bg-red-100 border border-red-500 text-red-700 py-2 px-3 mb-4 mx-2 shadow-md'
            }
            role="alert"
          >
            <FontAwesomeIcon icon={faExclamationTriangle} className="w-8 mr-2" />
            <span>An error has occurred, please try again later or contact us using alternative methods.</span>
          </div>
        )}
        <div className={twMerge('flex flex-col', props.classes_content)}>
          <div className="flex flex-col">
            <h3 className="mb-2 px-2 w-full text-lg uppercase font-bold text-sage-700">Personal Information</h3>
            <div className="my-2 px-2 w-full">
              <CommonLocationSelect
                name="store"
                label="Preferred Service Center"
                classContainer="flex-1"
                locationsOptionSet={props.servicesOptionSet}
                isRequired
                hideLabel
                usePlaceholder
              />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonInput
                type="text"
                label="First Name"
                placeholder="First Name*"
                name="firstName"
                isRequired
                hideLabel
              />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonInput
                type="text"
                label="Last Name"
                placeholder="Last Name*"
                name="lastName"
                isRequired
                hideLabel
              />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonMaskInput
                type="tel"
                mask="(999) 999-9999"
                label="Phone"
                placeholder="Phone*"
                name="phone"
                isRequired
                hideLabel
              />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonInput type="email" label="Email" placeholder="Email*" name="email" isRequired hideLabel />
            </div>
          </div>
          <div className="flex flex-col">
            <h3 className="my-2 px-2 w-full text-lg uppercase font-bold text-sage-700">Coach/RV Information</h3>
            <div className="my-2 px-2 w-full">
              <CommonInput
                type="number"
                label="Year"
                placeholder="Year"
                name="year"
                customOptions={{ min: 0, step: 1 }}
                hideLabel
              />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonInput type="text" label="Make" placeholder="Make" name="make" hideLabel />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonInput type="text" label="Model" placeholder="Model" name="model" hideLabel />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonInput type="text" label="VIN" placeholder="VIN" name="vin" hideLabel />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonTextarea label="Comments" name="comments" hideLabel />
            </div>
          </div>
          {props.display_sms_checkbox && (
            <div className="flex flex-row py-2">
              <BaseOptInCheckbox name="smsOptIn" nameDependency="phone" htmlContent={props.custom_sms_text} />
            </div>
          )}
          <div className="flex flex-col">
            <div className="p-2">
              <BaseSubmit {...props} formNameSlug="form-parts" styleClasses={FormClasses.buttonNormalClasses} />
            </div>
          </div>
        </div>
      </div>
    </CommonForm>
  );
});
